import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import Pagination from "../components/Pagination";
import NoResults from "../components/NoResults";
import SearchAPI from "../api/search";
import { FaRegBuilding } from "react-icons/fa";
import { RiMedicineBottleLine, RiLeafLine, RiFilter3Line } from "react-icons/ri";
import TopBadgeIcon from "../components/commons/TopBadgeIcon";

/* ─── Type filter config ──────────────────────────────────────────────────── */

// const TYPE_FILTERS = [
//   { value: "all", label: "All results", icon: RiFilter3Line },
//   { value: "tradenames", label: "Brand names", icon: RiMedicineBottleLine },
//   { value: "ingredients", label: "Ingredients", icon: RiLeafLine },
//   { value: "manufacturers", label: "Manufacturers", icon: FaRegBuilding },
// ];
const TYPE_FILTERS = []
const SORT_OPTIONS = [
  { value: "relevance", label: "Most relevant" },
  { value: "name", label: "A → Z" },
  { value: "name_desc", label: "Z → A" },
];

/* ─── Main component ──────────────────────────────────────────────────────── */

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || "";
  const searchType = searchParams.get("type") || "all";
  const orderByParam = searchParams.get("orderBy") || "relevance";
  const pageParam = searchParams.get("page");

  const [currentPage, setCurrentPage] = useState(
    pageParam ? parseInt(pageParam) - 1 : 0
  );
  const [searchResults, setSearchResults] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalItems: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeType, setActiveType] = useState(searchType);
  const [orderBy, setOrderBy] = useState(orderByParam);

  // Sync type from URL
  useEffect(() => {
    setActiveType(searchType);
  }, [searchType]);

  // Fetch search results
  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        setIsLoading(true);
        setError(null);
        let data;

        switch (activeType) {
          case "tradenames":
            data = await SearchAPI.searchTradeNames(
              searchQuery,
              currentPage + 1,
              orderBy
            );
            break;
          case "manufacturers":
            data = await SearchAPI.searchManufacturers(
              searchQuery,
              currentPage + 1,
              orderBy
            );
            break;
          case "ingredients":
            data = await SearchAPI.searchIngredients(
              searchQuery,
              currentPage + 1,
              orderBy
            );
            break;
          default:
            data = await SearchAPI.searchAll(
              searchQuery,
              currentPage + 1,
              orderBy
            );
        }

        setSearchResults(data.results || []);
        setPagination(
          data.pagination || { page: 1, totalItems: 0, totalPages: 0 }
        );
      } catch (err) {
        setError("Unable to load search results. Please try again.");
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (searchQuery) {
      fetchSearchResults();
    } else {
      setIsLoading(false);
      setSearchResults([]);
    }
  }, [searchQuery, activeType, orderBy, currentPage]);

  // Update URL when filters change
  useEffect(() => {
    setSearchParams(
      {
        q: searchQuery,
        type: activeType,
        page: currentPage + 1,
        orderBy: orderBy,
      },
      { replace: true }
    );
  }, [activeType, orderBy, currentPage, searchQuery, setSearchParams]);

  const handlePageChange = (selectedPage) => {
    if (selectedPage >= 0 && selectedPage < pagination.totalPages) {
      setCurrentPage(selectedPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleTypeChange = (type) => {
    setActiveType(type);
    setCurrentPage(0);
  };

  const handleSortChange = (e) => {
    setOrderBy(e.target.value);
    setCurrentPage(0);
  };

  return (
    <PageLayout
      className="pb-16"
      searchBarClassName="md:w-full md:max-w-[700px]"
      showSearch={true}
      initialQuery={searchQuery}
      initialType={searchType}
      pageTitle={
        searchQuery ? `Search results for "${searchQuery}"` : "Search"
      }
      showGradient={false}
    >
      {/* Filters bar */}
      <div className="mt-6 mb-6">
        {/* Type filter pills */}
        <div className="flex items-start gap-2 flex-wrap">
             {/* Results count */}
          {!isLoading && pagination.totalItems > 0 && (
            <p className="mb-4 text-16 font-text text-secondary/70">
              {pagination.totalItems} results for{" "}
              <span className="font-medium text-secondary">"{searchQuery}"</span>
            </p>
          )}

          {/* Sort dropdown (right-aligned) */}
          <div className="ml-auto">
            <select
              value={orderBy}
              onChange={handleSortChange}
              className="px-3 py-2 text-16 font-text text-secondary/70 bg-white border border-secondary/10 rounded-lg cursor-pointer hover:border-secondary/20 focus:outline-none focus:border-primary"
            >
              {SORT_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <SearchSkeleton />
      ) : error ? (
        <div className="py-12 text-center max-w-lg mx-auto">
          <div className="flex items-center justify-center size-14 rounded-2xl bg-red-50 mx-auto mb-4">
            <span className="text-red-500 text-24">!</span>
          </div>
          <h3 className="text-20 font-semibold text-secondary mb-2">
            Something went wrong
          </h3>
          <p className="text-16 font-text text-secondary/80 mb-6 leading-relaxed">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-3 text-16 font-text font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors min-h-[44px]"
          >
            Reload page
          </button>
        </div>
      ) : searchResults.length > 0 ? (
        <>
          <div className="space-y-3">
            {searchResults.map((result, i) => (
              <ResultItem
                key={result.uri || i}
                result={result}
                activeType={activeType}
                searchQuery={searchQuery}
              />
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex justify-center mt-10">
              <Pagination
                pageCount={pagination.totalPages}
                currentPage={pagination.page - 1}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      ) : searchQuery ? (
        <NoResults
          searchQuery={searchQuery}
          message="No matches found. Try a different spelling or search for the brand name instead of the generic."
        />
      ) : (
        <div className="py-16 text-center">
          <p className="text-18 font-text text-secondary/70">
            Type a medicine name to search
          </p>
        </div>
      )}
    </PageLayout>
  );
}

/* ─── Result item ─────────────────────────────────────────────────────────── */

function ResultItem({ result, activeType, searchQuery }) {
  // Determine the result type
  let type = result.type;
  if (!type && activeType !== "all") {
    type =
      activeType === "tradenames"
        ? "tradename"
        : activeType === "ingredients"
        ? "ingredient"
        : "manufacturer";
  }

  const config = {
    tradename: {
      icon: RiMedicineBottleLine,
      iconColor: "text-primary",
      badgeColor: "bg-primary/8 text-primary",
      label: "Brand name",
      link: `/tradenames/${result.uri}`,
    },
    ingredient: {
      icon: RiLeafLine,
      iconColor: "text-emerald-600",
      badgeColor: "bg-emerald-50 text-emerald-700",
      label: "Active ingredient",
      link: `/ingredients/${result.uri}`,
    },
    manufacturer: {
      icon: FaRegBuilding,
      iconColor: "text-purple-600",
      badgeColor: "bg-purple-50 text-purple-700",
      label: "Manufacturer",
      link: `/manufacturers/${result.uri}`,
    },
  };

  const c = config[type] || config.tradename;
  const Icon = c.icon;
  const isFeatured = result.is_sponsor || result.is_top_brand || result.is_top_manufacturer;

  return (
    <Link
      to={c.link}
      className="group block p-5 max-md:p-4 bg-white rounded-xl border border-secondary/8 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200"
    >
      <div className="flex items-start gap-4 max-md:gap-3">
        {/* Icon */}
        <div
          className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-secondary/5 ${c.iconColor}`}
        >
          <Icon className="size-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-18 max-md:text-16 font-semibold text-secondary group-hover:text-primary transition-colors">
              {highlightText(result.name, searchQuery)}
            </span>
            {isFeatured && <TopBadgeIcon />}
          </div>

          {/* Trade name details */}
          {type === "tradename" && (
            <>
              {result.manufacturers?.length > 0 && (
                <p className="mt-1 text-16 max-md:text-14 font-text text-secondary/70">
                  {result.manufacturers.map((m) => m.name).join(", ")}
                </p>
              )}
              {result.ingredients?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {result.ingredients.slice(0, 4).map((ingredient) => (
                    <span
                      key={ingredient.uri}
                      className="px-2 py-0.5 rounded-md text-14 font-text text-secondary/70 bg-secondary/5"
                    >
                      {ingredient.name}
                    </span>
                  ))}
                  {result.ingredients.length > 4 && (
                    <span className="px-2 py-0.5 rounded-md text-14 font-text text-secondary/70 bg-secondary/5">
                      +{result.ingredients.length - 4} more
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {/* Ingredient details */}
          {type === "ingredient" && result.codes?.length > 0 && (
            <p className="mt-1 text-16 max-md:text-14 font-text text-secondary/70">
              {result.codes.length} classification codes
            </p>
          )}
        </div>

        {/* Type badge */}
        <span
          className={`flex-shrink-0 px-3 py-1 rounded-full text-14 font-text font-medium ${c.badgeColor}`}
        >
          {c.label}
        </span>
      </div>
    </Link>
  );
}

/* ─── Highlight helper ────────────────────────────────────────────────────── */

function highlightText(text, query) {
  if (!query || !text) return text;
  try {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escaped})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark
          key={i}
          className="bg-yellow-100 text-secondary rounded-sm px-0.5"
        >
          {part}
        </mark>
      ) : (
        part
      )
    );
  } catch {
    return text;
  }
}

/* ─── Skeleton ────────────────────────────────────────────────────────────── */

function SearchSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="p-5 bg-white rounded-xl border border-secondary/8 animate-pulse"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-secondary/8" />
            <div className="flex-1">
              <div className="w-48 h-5 rounded bg-secondary/8 mb-2" />
              <div className="w-32 h-4 rounded bg-secondary/6" />
            </div>
            <div className="w-24 h-6 rounded-full bg-secondary/6" />
          </div>
        </div>
      ))}
    </div>
  );
}
